import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { SessionUser } from "@demore/shared/types";
import { api } from "./api";

// CEO Rev 1.0 — feature flags delivered by /api/auth/me (deploy-dark defaults).
// workspaceStrict = S14 cutover (mandatory workspace, global pages retired).
export type FeatureFlags = {
  campaignWorkspaces: boolean;
  ssoEnabled: boolean;
  workspaceStrict: boolean;
};
const DEFAULT_FLAGS: FeatureFlags = {
  campaignWorkspaces: false,
  ssoEnabled: false,
  workspaceStrict: false,
};

type AuthState = {
  user: SessionUser | null;
  loading: boolean;
  flags: FeatureFlags;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const data = await api.get<{
        user: SessionUser | null;
        flags?: Partial<FeatureFlags>;
      }>("/api/auth/me");
      setUser(data.user);
      setFlags({
        campaignWorkspaces: !!data.flags?.campaignWorkspaces,
        ssoEnabled: !!data.flags?.ssoEnabled,
        workspaceStrict: !!data.flags?.workspaceStrict,
      });
    } catch {
      setUser(null);
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function logout() {
    await api.post("/api/auth/logout");
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, flags, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside <AuthProvider>");
  return ctx;
}
